import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { Select } from '../components/Select';
import { Modal } from '../components/Modal';
import { BooleanIcon } from '../components/BooleanIcon';
import { logout } from '../redux/slices/admin/operations';
import styles from './Dashboard.module.scss';

const FILTER_OPTIONS = [
  { value: 'users', label: 'Users' },
  { value: 'chats', label: 'Chats' },
  { value: 'messages', label: 'Messages' },
  { value: 'tickets', label: 'Tickets' },
  { value: 'prices', label: 'Prices' },
];

export const DashboardPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('users');
  const [data, setData] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [sortField, setSortField] = useState('all');
  const [action, setAction] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalValue, setModalValue] = useState('');
  const [modalType, setModalType] = useState('');
  const [urlSearch, setUrlSearch] = useState('');

  const fetchByFilter = async selected => {
    try {
      let response;

      if (selected === 'users') {
        response = await api.post('/admin/users', { filter: {} });
      } else if (selected === 'chats') {
        response = await api.get('/admin/conversations', {
          params: { filter: {} },
        });
      } else if (selected === 'messages') {
        // Example conversation id; in a real app this would come from selected chat
        const conversationId = 'example_conversation_id';
        response = await api.get(`/admin/messages/${conversationId}`);
      } else if (selected === 'tickets') {
        response = await api.get('/admin/tickets');
      }

      setData(response?.data ?? null);
    } catch (error) {
      setData({ error: error.response?.data ?? 'Request failed' });
    }
  };

  const handleFilterChange = async event => {
    const selected = event.target.value;
    if (selected === 'prices') {
      navigate('/admin/prices');
      return;
    }
    setFilter(selected);
    setSelectedUserId(null);
    setSelectedTicketId(null);
    setSortField(selected === 'users' ? 'all' : '');
    setUrlSearch('');
    fetchByFilter(selected);
  };

  const handleUserSelect = userId => {
    setSelectedUserId(prev => (prev === userId ? null : userId));
  };

  const handleActionChange = event => {
    const selectedAction = event.target.value;
    setAction(selectedAction);

    if (
      ['changeBalance', 'updateFreeChats', 'updateFreeAiChats'].includes(
        selectedAction
      )
    ) {
      if (!selectedUserId) {
        // Simple UX for now; could be replaced with toast
        alert('Please select a user first.');
        return;
      }
      setModalType(selectedAction);
      setModalValue('');
      setIsModalOpen(true);
      return;
    }

    if (selectedAction === 'showKnowledgeBase') {
      if (!selectedUserId) {
        alert('Please select a user first.');
        return;
      }
      navigate(
        `/admin/knowledges?userId=${encodeURIComponent(selectedUserId)}`
      );
      return;
    }

    if (selectedAction === 'showSystemInstruction') {
      if (!selectedUserId) {
        alert('Please select a user first.');
        return;
      }
      navigate(
        `/admin/instruction?userId=${encodeURIComponent(selectedUserId)}`
      );
      return;
    }

    if (selectedAction === 'showConversations') {
      if (!selectedUserId) {
        alert('Please select a user first.');
        return;
      }
      navigate(
        `/admin/conversations?userId=${encodeURIComponent(selectedUserId)}`
      );
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setModalType('');
    setModalValue('');
  };

  const handleResolveTicketClick = () => {
    if (!selectedTicketId) return;
    setModalType('resolveTicket');
    setModalValue('');
    setIsModalOpen(true);
  };

  const handleModalConfirm = async () => {
    if (modalType === 'resolveTicket') {
      if (!selectedTicketId) return;
      try {
        await api.patch(`/admin/tickets/${selectedTicketId}`, {
          text: modalValue,
        });
        await fetchByFilter('tickets');
        setSelectedTicketId(null);
        handleModalClose();
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to resolve ticket');
      }
      return;
    }

    if (!selectedUserId || !modalType) return;

    const userId = selectedUserId;
    const numericValue = Number(modalValue);
    if (Number.isNaN(numericValue)) {
      alert('Please enter a valid number.');
      return;
    }

    try {
      if (modalType === 'changeBalance') {
        await api.patch(`/admin/users/${userId}/balance`, {
          balance: numericValue,
        });
      } else if (modalType === 'updateFreeChats') {
        await api.patch(`/admin/users/${userId}/freeChats`, {
          freeChats: numericValue,
        });
      } else if (modalType === 'updateFreeAiChats') {
        await api.patch(`/admin/users/${userId}/aiFreeChats`, {
          aiFreeChats: numericValue,
        });
      }

      await fetchByFilter('users');
      setSelectedUserId(null);
      handleModalClose();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update user');
    }
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  const getSortedUsers = () => {
    if (!Array.isArray(data?.users)) {
      return [];
    }

    let users = [...data.users];

    if (urlSearch) {
      const query = urlSearch.toLowerCase();
      users = users.filter(user => {
        const urlValue = (user.url || user.siteUrl || '').toLowerCase();
        return urlValue.includes(query);
      });
    }

    if (!sortField || sortField === 'all') {
      return users;
    }

    if (sortField === 'url') {
      return users
        .filter(user => user.url || user.siteUrl)
        .sort((a, b) => {
          const aUrl = a.url || a.siteUrl || '';
          const bUrl = b.url || b.siteUrl || '';
          return aUrl.localeCompare(bUrl);
        });
    }

    if (['needLearnAi', 'aiEnabled', 'aiLearnt'].includes(sortField)) {
      return users
        .filter(user => Boolean(user[sortField]))
        .sort((a, b) => {
          const aEmail = a.email || '';
          const bEmail = b.email || '';
          return aEmail.localeCompare(bEmail);
        });
    }

    return users;
  };

  useEffect(() => {
    fetchByFilter('users');
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.filters}>
        <div className={styles.filtersTopRow}>
          <button
            type="button"
            onClick={handleLogout}
            style={{
              padding: '0.4rem 0.9rem',
              borderRadius: '9999px',
              border: '1px solid #e5e7eb',
              backgroundColor: '#ffffff',
              fontSize: '0.85rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Logout
          </button>
        </div>
        <div className={styles.filtersControls}>
          <div className={styles.filtersGroup}>
            <p className={styles.filtersLabel}>Data</p>
            <Select
              value={filter}
              onChange={handleFilterChange}
              options={FILTER_OPTIONS}
              placeholder="Select data type"
            />
          </div>

          {filter === 'users' && (
            <div className={styles.filtersGroup}>
              <p className={styles.filtersLabel}>Sort</p>
              <Select
                value={sortField}
                onChange={event => setSortField(event.target.value)}
                options={[
                  { value: 'all', label: 'all' },
                  { value: 'needLearnAi', label: 'needLearnAi' },
                  { value: 'aiEnabled', label: 'aiEnabled' },
                  { value: 'aiLearnt', label: 'aiLearnt' },
                  { value: 'url', label: 'url' },
                ]}
                placeholder="Select sort field"
              />
            </div>
          )}

          {filter === 'users' && (
            <div className={styles.filtersGroup}>
              <p className={styles.filtersLabel}>Actions</p>
              <Select
                value={action}
                onChange={handleActionChange}
                options={[
                  { value: 'changeBalance', label: 'change balance' },
                  { value: 'updateFreeChats', label: 'update freeChats' },
                  { value: 'updateFreeAiChats', label: 'update freeAiChats' },
                  { value: 'showKnowledgeBase', label: 'show knowledge base' },
                  { value: 'showConversations', label: 'show conversations' },
                  {
                    value: 'showSystemInstruction',
                    label: 'show system instruction',
                  },
                ]}
                placeholder="Select action"
              />
            </div>
          )}
        </div>

        {filter === 'users' && (
          <div style={{ marginTop: '1rem', maxWidth: '260px' }}>
            <p className={styles.filtersLabel}>Search by URL</p>
            <input
              type="text"
              value={urlSearch}
              onChange={e => setUrlSearch(e.target.value)}
              placeholder="Type part of URL..."
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                borderRadius: '0.5rem',
                border: '1px solid #d1d5db',
                fontSize: '0.9rem',
              }}
            />
          </div>
        )}
      </div>

      <div className={styles.content}>
        <Modal
          isOpen={isModalOpen}
          title={
            modalType === 'changeBalance'
              ? 'Change balance'
              : modalType === 'updateFreeChats'
                ? 'Update freeChats'
                : modalType === 'updateFreeAiChats'
                  ? 'Update freeAiChats'
                  : modalType === 'resolveTicket'
                    ? 'Resolve ticket'
                    : ''
          }
          onClose={handleModalClose}
          onConfirm={handleModalConfirm}
          confirmLabel={modalType === 'resolveTicket' ? 'Resolve' : 'Save'}
        >
          {(modalType === 'changeBalance' ||
            modalType === 'updateFreeChats' ||
            modalType === 'updateFreeAiChats') && (
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  marginBottom: '0.25rem',
                }}
              >
                New value
              </label>
              <input
                type="number"
                value={modalValue}
                onChange={e => setModalValue(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #d1d5db',
                  fontSize: '0.9rem',
                }}
              />
            </div>
          )}
          {modalType === 'resolveTicket' && (
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  marginBottom: '0.25rem',
                }}
              >
                Resolve message (optional)
              </label>
              <textarea
                value={modalValue}
                onChange={e => setModalValue(e.target.value)}
                placeholder="Type text to send with resolve..."
                rows={4}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #d1d5db',
                  fontSize: '0.9rem',
                  resize: 'vertical',
                }}
              />
            </div>
          )}
        </Modal>

        {filter === 'users' && getSortedUsers().length > 0 && (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead className={styles.thead}>
                <tr>
                  <th className={styles.th}>Email</th>
                  <th className={styles.th}>URL</th>
                  <th className={styles.th}>AI</th>
                  <th className={styles.th}>needLearnAi</th>
                  <th className={styles.th}>aiLearnt</th>
                  <th className={styles.th}>Operators</th>
                  <th className={styles.th}>Free chats</th>
                  <th className={styles.th}>Free AI chats</th>
                  <th className={styles.th}>Balance</th>
                  <th className={styles.th}>Select</th>
                </tr>
              </thead>
              <tbody className={styles.tbody}>
                {getSortedUsers().map(user => (
                  <tr key={user._id || user.email}>
                    <td className={styles.td}>{user.email}</td>
                    <td className={styles.td}>
                      {user.url || user.siteUrl || '-'}
                    </td>
                    <td className={styles.td}>
                      <BooleanIcon value={user.aiEnabled} />
                    </td>
                    <td className={styles.td}>
                      <BooleanIcon value={user.needLearnAi} />
                    </td>
                    <td className={styles.td}>
                      <BooleanIcon value={user.aiLearnt} />
                    </td>
                    <td className={styles.td}>
                      {user.operatorsCount ??
                        user.maxOperators ??
                        user.operatorsAmount ??
                        '-'}
                    </td>
                    <td className={styles.td}>
                      {user.freeChats !== undefined ? user.freeChats : '-'}
                    </td>
                    <td className={styles.td}>
                      {user.aiFreeChats !== undefined ? user.aiFreeChats : '-'}
                    </td>
                    <td className={styles.td}>
                      {user.balance !== undefined ? user.balance : '-'}
                    </td>
                    <td className={styles.td}>
                      <input
                        type="checkbox"
                        checked={selectedUserId === (user._id || user.email)}
                        onChange={() =>
                          handleUserSelect(user._id || user.email)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filter === 'users' &&
          Array.isArray(data?.users) &&
          getSortedUsers().length === 0 && (
            <p className={styles.emptyState}>
              No users found for the current filter.
            </p>
          )}

        {filter === 'tickets' && selectedTicketId && (
          <div style={{ marginBottom: '1rem' }}>
            <button
              type="button"
              onClick={handleResolveTicketClick}
              style={{
                padding: '0.45rem 0.9rem',
                borderRadius: '9999px',
                border: 'none',
                backgroundColor: '#2563eb',
                color: '#ffffff',
                fontSize: '0.85rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Resolve ticket
            </button>
          </div>
        )}

        {filter === 'tickets' &&
          Array.isArray(data?.tickets) &&
          data.tickets.length > 0 && (
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead className={styles.thead}>
                  <tr>
                    <th className={styles.th}>Email</th>
                    <th className={styles.th}>Ticket</th>
                    <th className={styles.th}>Resolved</th>
                    <th className={styles.th}>Resolution</th>
                    <th className={styles.th}>Select</th>
                  </tr>
                </thead>
                <tbody className={styles.tbody}>
                  {data.tickets.map(ticket => (
                    <tr key={ticket._id}>
                      <td className={styles.td}>{ticket.email}</td>
                      <td className={styles.td}>{ticket.ticket}</td>
                      <td className={styles.td}>
                        {typeof ticket.resolved === 'boolean'
                          ? ticket.resolved
                            ? 'Yes'
                            : 'No'
                          : '-'}
                      </td>
                      <td className={styles.td}>
                        {ticket.resolution != null && ticket.resolution !== ''
                          ? ticket.resolution
                          : '-'}
                      </td>
                      <td className={styles.td}>
                        <input
                          type="checkbox"
                          checked={selectedTicketId === ticket._id}
                          onChange={() =>
                            setSelectedTicketId(prev =>
                              prev === ticket._id ? null : ticket._id
                            )
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        {filter === 'tickets' &&
          Array.isArray(data?.tickets) &&
          data.tickets.length === 0 && (
            <p className={styles.emptyState}>
              No tickets found for the current filter.
            </p>
          )}

        {/* Fallback debug view for other filters */}
        {filter !== 'users' && filter !== 'tickets' && data && (
          <pre style={{ marginTop: '1rem', fontSize: '0.8rem' }}>
            {JSON.stringify(data, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
};
